import { ENDPOINT } from 'src/constants/api';

import { baseApi } from '../base';

const unwrapApiData = <T>(response: unknown): T => {
  if (
    response &&
    typeof response === 'object' &&
    'data' in response &&
    (response as { data?: unknown }).data !== undefined
  ) {
    return (response as { data: T }).data;
  }

  return response as T;
};

export type StockPhotoImportBatchStatus =
  | 'queued'
  | 'processing'
  | 'completed'
  | 'completed_with_errors'
  | 'failed'
  | 'canceled';

export type StockPhotoImportScope = 'account' | 'vehicle';

export type StockPhotoImportCounts = {
  queued: number;
  processing: number;
  imported: number;
  skipped: number;
  failed: number;
  canceled: number;
};

export type StockPhotoImportAccount = {
  id: number;
  name: string;
  avatar: string | null;
};

export type StockPhotoImportBatch = {
  id: string;
  operation_type: 'stock_photo_import';
  scope: StockPhotoImportScope;
  account_id: number;
  vehicle_id: number | null;
  status: StockPhotoImportBatchStatus;
  total: number;
  processed: number;
  progress: number;
  counts: StockPhotoImportCounts;
  imported_images: number;
  skipped_reasons: Record<string, number>;
  created_at: string;
  completed_at: string | null;
  canceled_at: string | null;
  status_url: string;
  account?: StockPhotoImportAccount;
};

export type CreateAccountStockPhotoImportParams = {
  accountId: number;
  idempotencyKey: string;
};

export const stockPhotoImportBatchesPath = () =>
  `${ENDPOINT}/vehicles/import-stock-photos/batches`;

export const isActiveBatchStatus = (status: StockPhotoImportBatchStatus) =>
  status === 'queued' || status === 'processing';

export const isTerminalBatchStatus = (status: StockPhotoImportBatchStatus) =>
  status === 'completed' ||
  status === 'completed_with_errors' ||
  status === 'failed' ||
  status === 'canceled';

const stockPhotoImportsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getOpenStockPhotoImportBatches: builder.query<StockPhotoImportBatch[], void>({
      query: () => ({
        url: `${stockPhotoImportBatchesPath()}?status=open`,
        method: 'GET',
      }),
      keepUnusedDataFor: 0,
      transformResponse: unwrapApiData<StockPhotoImportBatch[]>,
    }),
    getStockPhotoImportBatch: builder.query<StockPhotoImportBatch, string>({
      query: (batchId) => ({
        url: `${stockPhotoImportBatchesPath()}/${batchId}`,
        method: 'GET',
      }),
      keepUnusedDataFor: 0,
      transformResponse: unwrapApiData<StockPhotoImportBatch>,
    }),
    createAccountStockPhotoImport: builder.mutation<
      StockPhotoImportBatch,
      CreateAccountStockPhotoImportParams
    >({
      query: ({ accountId, idempotencyKey }) => ({
        url: `${ENDPOINT}/vehicles/import-stock-photos`,
        method: 'POST',
        body: { account: accountId },
        headers: {
          'Idempotency-Key': idempotencyKey,
        },
      }),
      transformResponse: unwrapApiData<StockPhotoImportBatch>,
    }),
    cancelStockPhotoImportBatch: builder.mutation<StockPhotoImportBatch, string>({
      query: (batchId) => ({
        url: `${stockPhotoImportBatchesPath()}/${batchId}/cancel`,
        method: 'POST',
      }),
      transformResponse: unwrapApiData<StockPhotoImportBatch>,
    }),
  }),
});

export const {
  useLazyGetOpenStockPhotoImportBatchesQuery,
  useLazyGetStockPhotoImportBatchQuery,
  useCreateAccountStockPhotoImportMutation,
  useCancelStockPhotoImportBatchMutation,
} = stockPhotoImportsApi;

export default stockPhotoImportsApi;
